export type Product = {
  id: number
  name: string
  code: string
  slug: string
  category: string
  categorySlug: string
  image: string
  alt: string
  summary: string
  details: string[]
}

const defaultProducts: Product[] = [
  {
    id: 1,
    name: 'Hydraulic Knee Joint',
    code: 'A3-HKJ',
    slug: 'hydraulic-knee-joint-a3-hkj',
    category: 'Knee Joint',
    categorySlug: 'knee-joint',
    image: '/images/products/hydraulic-knee-joint-a3-hkj.webp',
    alt: 'A3-HKJ single-axis hydraulic prosthetic knee joint',
    summary: 'Single-axis hydraulic knee joint for reliable everyday walking and rehabilitation training.',
    details: [
      'Three adjustable working modes for rehabilitation, daily use and experienced users.',
      'Auto locks at 15-20 degrees of flexion during weight-bearing stance.',
      'Supports alternating stair descent and smoother slope walking.'
    ]
  },
  {
    id: 2,
    name: 'Lightweight Knee Joint',
    code: 'A3-R40',
    slug: 'lightweight-knee-joint-a3-r40',
    category: 'Knee Joint',
    categorySlug: 'knee-joint',
    image: '/images/products/lightweight-knee-joint-a3-r40.webp',
    alt: 'A3-R40 lightweight aluminium alloy prosthetic knee joint',
    summary: 'Aluminum alloy knee joint designed for trans-femoral amputees with low functional level.',
    details: [
      'Lightweight structure for reduced wearing burden.',
      'Locking system provides high stance phase stability.',
      'Built for safer basic mobility and daily support.'
    ]
  },
  {
    id: 3,
    name: 'Cosmetic Hand',
    code: 'A9-CSHHZ',
    slug: 'cosmetic-hand-a9-cshhz',
    category: 'Silicone Prosthesis',
    categorySlug: 'silicone-prosthesis',
    image: '/images/products/silicone-cosmetic-hand-a9-cshhz.webp',
    alt: 'A9-CSHHZ medical grade silicone cosmetic prosthetic hand',
    summary: 'Medical-grade silicone prosthetic hand with realistic skin texture and natural appearance.',
    details: [
      'Realistic fingerprints, skin texture and natural fingernails.',
      'Multiple skin tones available.',
      'Suitable for partial hand or half hand amputation.'
    ]
  },
  {
    id: 4,
    name: 'Cosmetic Sock',
    code: 'A6-VS',
    slug: 'cosmetic-sock-a6-vs',
    category: 'Socks',
    categorySlug: 'socks',
    image: '/images/products/prosthetic-cosmetic-sock-a6-vs.webp',
    alt: 'A6-VS high-elastic nylon prosthetic stump sock',
    summary: 'High-elastic nylon stump socks matched with silicone liners.',
    details: [
      'Reduces abrasion between liner and residual limb.',
      'Helps extend the service life of silicone liners.',
      'Soft daily-use accessory for prosthetic fitting.'
    ]
  },
  {
    id: 5,
    name: 'Carbon Fiber Cloth',
    code: 'A5-CFC',
    slug: 'carbon-fiber-cloth-a5-cfc',
    category: 'Material',
    categorySlug: 'material',
    image: '/images/products/carbon-fiber-cloth-a5-cfc.webp',
    alt: 'A5-CFC twill carbon fiber cloth for prosthetic socket lamination',
    summary: 'High tensile twill carbon fiber cloth for customized prosthetic socket lamination.',
    details: [
      'Essential raw material for prosthetic workshops.',
      'Suitable for custom socket fabrication.',
      'Twill carbon fiber structure with strong tensile performance.'
    ]
  },
  {
    id: 6,
    name: 'Carbon Fiber Foot',
    code: 'A1-CFLD',
    slug: 'carbon-fiber-foot-a1-cfld',
    category: 'Foot',
    categorySlug: 'foot',
    image: '/images/products/carbon-fiber-prosthetic-foot-a1-cfld.webp',
    alt: 'A1-CFLD dynamic carbon fiber prosthetic foot',
    summary: 'High activity dynamic carbon foot with strong rebound elasticity.',
    details: [
      'Helps reduce walking fatigue.',
      'Suitable for below-knee and above-knee amputees.',
      'Universal pyramid interface fits modular prosthetic limbs.'
    ]
  },
  {
    id: 7,
    name: 'Below Elbow Myoelectric Hand',
    code: 'A9-BEMH',
    slug: 'below-elbow-myoelectric-hand-a9-bemh',
    category: 'Myoelectric Hand',
    categorySlug: 'myoelectric-hand',
    image: '/images/products/below-elbow-myoelectric-hand-a9-bemh.webp',
    alt: 'A9-BEMH two-channel myoelectric prosthetic hand',
    summary: 'Two-channel myoelectric hand controlled by EMG signals from residual muscles.',
    details: [
      'Realizes stable gripping for daily life.',
      'Supports wrist rotation movement.',
      'Compatible with standard wrist adapters and silicone cosmetic sleeves.'
    ]
  },
  {
    id: 8,
    name: '3-Prong Rotatable Pyramid Receiver Adapter',
    code: 'A4-R41',
    slug: '3-prong-pyramid-receiver-adapter-a4-r41',
    category: 'Adapter',
    categorySlug: 'adapter',
    image: '/images/products/pyramid-receiver-adapter-a4-r41.webp',
    alt: 'A4-R41 three-prong rotatable pyramid receiver adapter',
    summary: 'Precision prosthetic connector for sockets and pylons with multi-angle adjustment.',
    details: [
      'Supports sagittal and frontal plane fine-tuning.',
      'Rotatable design enables easier assembly.',
      'Stable locking improves alignment and wearing comfort.'
    ]
  },
  {
    id: 9,
    name: 'Sach Foot Adapter',
    code: 'A2-R8=M10',
    slug: 'sach-foot-adapter-a2-r8-m10',
    category: 'Adapter',
    categorySlug: 'adapter',
    image: '/images/products/sach-foot-adapter-a2-r8-m10.webp',
    alt: 'A2-R8=M10 SACH foot sliding and angular alignment adapter',
    summary: 'Sliding and angular alignment adapter for lower limb prosthetic balance correction.',
    details: [
      'Enables sliding and multi-angle fine adjustment.',
      'Standard pyramid interface with locking knob.',
      'Connects sockets, pylons and knee joints reliably.'
    ]
  },
  {
    id: 10,
    name: 'Lock Tube Clamp Adapter',
    code: 'A4-R21',
    slug: 'lock-tube-clamp-adapter-a4-r21',
    category: 'Adapter',
    categorySlug: 'adapter',
    image: '/images/products/tube-clamp-adapter-a4-r21.webp',
    alt: 'A4-R21 lock tube clamp adapter with dual locking screws',
    summary: 'Universal pyramid connector with dual locking screws and quick assembly design.',
    details: [
      'Compatible with most modular prosthetic parts.',
      'Dual locking screws ensure stable load-bearing.',
      'Side knob supports fast assembly and removal.'
    ]
  },
  {
    id: 11,
    name: 'Prosthetic Liner',
    code: 'ASL',
    slug: 'prosthetic-liner-asl',
    category: 'Liner',
    categorySlug: 'liner',
    image: '/images/products/silicone-prosthetic-liner-asl.webp',
    alt: 'ASL air cushion silicone prosthetic liner',
    summary: 'Air cushion silicone liner with shock absorption, suspension and anti-friction comfort.',
    details: [
      'Improves wearing comfort for residual limbs.',
      'Provides stable suspension and cushioning.',
      'Popular in Africa, Middle East and South America markets.'
    ]
  },
  {
    id: 12,
    name: 'Shuttle Lock',
    code: 'A6-A10',
    slug: 'shuttle-lock-a6-a10',
    category: 'Shuttle Lock',
    categorySlug: 'shuttle-lock',
    image: '/images/products/prosthetic-shuttle-lock-a6-a10.webp',
    alt: 'A6-A10 nylon plate prosthetic shuttle lock',
    summary: 'Mechanical locking device for anchoring silicone or gel liners inside prosthetic sockets.',
    details: [
      'Prevents residual limb slipping during ambulation.',
      'Wear-resistant and corrosion-resistant nylon plate.',
      'Suitable for below-knee prostheses and safer suspension.'
    ]
  }
]

function loadProducts(): Product[] {
  if (typeof window === 'undefined') return defaultProducts
  try {
    const saved = window.localStorage.getItem('angeltech-admin-products')
    if (!saved) return defaultProducts
    const parsed = JSON.parse(saved)
    return Array.isArray(parsed) ? parsed : defaultProducts
  } catch {
    return defaultProducts
  }
}

export let products: Product[] = loadProducts()

export let featuredProducts = products.filter((product) => [1, 6, 7, 11].includes(product.id))

export function replaceProducts(nextProducts: Product[]) {
  products = nextProducts
  featuredProducts = products.filter((product) => [1, 6, 7, 11].includes(product.id))
}

/** All categories in first-appearance order, with slug and product count. */
export function getCategories(source: Product[] = products) {
  const order: string[] = []
  const slugs = new Map<string, string>()
  const representative = new Map<string, Product>()
  const count = new Map<string, number>()

  for (const product of source) {
    if (!representative.has(product.category)) {
      representative.set(product.category, product)
      order.push(product.category)
      slugs.set(product.category, product.categorySlug)
    }
    count.set(product.category, (count.get(product.category) ?? 0) + 1)
  }

  return order.map((category) => ({
    category,
    slug: slugs.get(category) as string,
    representative: representative.get(category) as Product,
    count: count.get(category) as number
  }))
}

export function getProductBySlug(slug: string) {
  return products.find((product) => product.slug === slug)
}

export function getCategoryBySlug(slug: string) {
  const match = products.find((product) => product.categorySlug === slug)
  if (!match) return undefined
  return {
    category: match.category,
    slug: match.categorySlug,
    items: products.filter((product) => product.categorySlug === slug)
  }
}

export { defaultProducts }
